import { FormEvent, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Edit, Play, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@/components/ui/Button';
import DataTable from '@/components/ui/DataTable';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import Select from '@/components/ui/Select';
import { TableColumn } from '@/types';
import { formatDateTime } from '@/utils/formatters';
import TVChannelScrapingLayout from '@/pages/content/TVChannelScrapingLayout';
import {
  useChannelScrapingServers,
  useChannelScrapingTask,
  useChannelScrapingTasks,
  useCreateChannelScrapingTask,
  useDeleteChannelScrapingTask,
  useRunChannelScrapingTask,
  useUpdateChannelScrapingTask,
  channelScrapingKeys,
} from '@/hooks/useChannelScraping';
import { useSelectedScrapingServer } from '@/hooks/useSelectedScrapingServer';
import {
  channelScrapingService,
  ChannelScrapingTask,
  ChannelScrapingTaskPayload,
  ChannelScrapingTaskRunSummary,
} from '@/services/channelScrapingService';

interface TaskHeaderRow {
  id: string;
  key: string;
  value: string;
}

interface TaskFormState {
  streamName: string;
  url: string;
  stopPatternRequest: string;
  frequencyHours: string;
  targetServerId: string;
  userAgent: string;
  headers: TaskHeaderRow[];
}

const emptyForm: TaskFormState = {
  streamName: '',
  url: '',
  stopPatternRequest: '',
  frequencyHours: '',
  targetServerId: '',
  userAgent: '',
  headers: [],
};

const createHeaderRow = (key = '', value = ''): TaskHeaderRow => ({
  id: typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  key,
  value,
});

const mapHeadersToRows = (headers?: Record<string, string> | null): TaskHeaderRow[] =>
  Object.entries(headers ?? {}).map(([key, value]) => createHeaderRow(key, value));

const mapRowsToHeaders = (headers: TaskHeaderRow[]): Record<string, string> =>
  headers.reduce<Record<string, string>>((accumulator, header) => {
    const key = header.key.trim();
    const value = header.value.trim();
    if (!key || !value) {
      return accumulator;
    }
    accumulator[key] = value;
    return accumulator;
  }, {});

const lastStatusClass = (status?: string | null) => {
  if (status === 'success') return 'bg-green-100 text-green-800';
  if (status === 'nomatch') return 'bg-yellow-100 text-yellow-800';
  if (status === 'error') return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-700';
};

const lastStatusLabel = (status?: string | null) => {
  if (status === 'success') return 'OK';
  if (status === 'nomatch') return 'Sin match';
  if (status === 'error') return 'Error';
  if (status === 'idle') return 'Inactiva';
  return 'Sin estado';
};

const deliveryModeLabel = (mode?: string | null) => {
  if (mode === 'ffmpeg') return 'FFmpeg';
  if (mode === 'direct') return 'Directa';
  return '-';
};

const deliveryModeClass = (mode?: string | null) => {
  if (mode === 'ffmpeg') return 'bg-blue-100 text-blue-800';
  if (mode === 'direct') return 'bg-gray-100 text-gray-700';
  return 'bg-gray-50 text-gray-500';
};

const runStatusClass = (status?: string | null) => {
  if (status === 'success') return 'bg-green-100 text-green-800';
  if (status === 'nomatch') return 'bg-yellow-100 text-yellow-800';
  if (status === 'error') return 'bg-red-100 text-red-800';
  if (status === 'running') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-700';
};

const runStatusLabel = (status?: string | null) => {
  if (status === 'success') return 'OK';
  if (status === 'nomatch') return 'Sin match';
  if (status === 'error') return 'Error';
  if (status === 'running') return 'Ejecutando';
  if (status === 'idle') return 'Inactiva';
  if (status === 'queued') return 'En cola';
  return status || 'Sin estado';
};

const isTerminalRunStatus = (status?: string | null) =>
  status === 'success' || status === 'nomatch' || status === 'error';

const lineClassName = (line: string) => {
  const normalized = line.toLowerCase();
  if (normalized.includes(' error') || normalized.includes('[error]')) {
    return 'text-red-400';
  }
  if (normalized.includes(' warn') || normalized.includes('[warn]') || normalized.includes('[warning]')) {
    return 'text-yellow-300';
  }
  if (normalized.includes(' status=success') || normalized.includes(' ok=true')) {
    return 'text-green-300';
  }
  if (normalized.includes('[task-run]')) {
    return 'text-cyan-300';
  }
  return 'text-gray-200';
};

const formatServerResponse = (value: unknown) => {
  if (value == null) {
    return '-';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const TVChannelScrapingTasks = () => {
  const queryClient = useQueryClient();
  const { selectedScrapingServerId } = useSelectedScrapingServer();
  const { data: tasks = [], isLoading, isError, error, refetch, isFetching } = useChannelScrapingTasks(selectedScrapingServerId);
  const { data: targetServers = [] } = useChannelScrapingServers(selectedScrapingServerId);
  const createMutation = useCreateChannelScrapingTask();
  const updateMutation = useUpdateChannelScrapingTask();
  const deleteMutation = useDeleteChannelScrapingTask();
  const runMutation = useRunChannelScrapingTask();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ChannelScrapingTask | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search.trim().toLowerCase());
  const [form, setForm] = useState<TaskFormState>(emptyForm);
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [runTaskLabel, setRunTaskLabel] = useState<string>('');
  const [runSummary, setRunSummary] = useState<ChannelScrapingTaskRunSummary | null>(null);
  const [runLogs, setRunLogs] = useState<string[]>([]);
  const [runError, setRunError] = useState<string | null>(null);
  const [isRunStarting, setIsRunStarting] = useState(false);
  const [isRunStreamConnecting, setIsRunStreamConnecting] = useState(false);
  const runStreamCleanupRef = useRef<(() => void) | null>(null);
  const runLogsContainerRef = useRef<HTMLDivElement | null>(null);
  const { data: editingTaskDetails, isLoading: isEditingTaskLoading, error: editingTaskError } = useChannelScrapingTask(editingTaskId ?? undefined, editingTask?.scrapingServerId);

  const targetServerOptions = useMemo(
    () => targetServers.map((server) => ({ value: server.id, label: server.name })),
    [targetServers]
  );
  const targetServerLookup = useMemo(
    () => new Map(targetServers.flatMap((server) => [
      [server.id, server] as const,
      [server.name, server] as const,
    ])),
    [targetServers]
  );
  const getTaskServerLabel = useCallback((task: ChannelScrapingTask) => {
    return task.serverId ? targetServerLookup.get(task.serverId)?.name || task.serverId : '';
  }, [targetServerLookup]);

  const getTaskScrapingServerLabel = useCallback((task: ChannelScrapingTask) => {
    return task.scrapingServerName || task.scrapingServerId || '';
  }, []);

  const filteredTasks = useMemo(() => {
    if (!deferredSearch) return tasks;
    return tasks.filter((task) =>
      [task.streamName, getTaskServerLabel(task), getTaskScrapingServerLabel(task), task.url, task.stopPatternRequest, task.lastStatus, task.lastDeliveryMode, task.lastMatchedUrl, task.lastVideoUrl, task.lastAudioUrl, task.lastError]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(deferredSearch)
    );
  }, [deferredSearch, getTaskScrapingServerLabel, getTaskServerLabel, tasks]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const openCreateModal = () => {
    setEditingTask(null);
    setEditingTaskId(null);
    setForm({
      ...emptyForm,
      targetServerId: targetServers[0]?.id || '',
    });
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (task: ChannelScrapingTask) => {
    setEditingTask(task);
    setEditingTaskId(task.id);
    setForm(emptyForm);
    setSubmitError(null);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!editingTaskDetails) {
      return;
    }

    setEditingTask(editingTaskDetails);
    setForm({
      streamName: editingTaskDetails.streamName || '',
      url: editingTaskDetails.url,
      stopPatternRequest: editingTaskDetails.stopPatternRequest || '',
      frequencyHours: editingTaskDetails.frequencyHours != null ? String(editingTaskDetails.frequencyHours) : '',
      targetServerId: editingTaskDetails.serverId || '',
      userAgent: editingTaskDetails.userAgent || '',
      headers: mapHeadersToRows(editingTaskDetails.httpHeaders),
    });
  }, [editingTaskDetails]);

  useEffect(() => {
    if (editingTaskError) {
      setSubmitError(editingTaskError instanceof Error ? editingTaskError.message : 'No se pudo cargar la tarea');
    }
  }, [editingTaskError]);

  const closeModal = () => {
    if (isSubmitting) return;
    setIsModalOpen(false);
    setEditingTask(null);
    setEditingTaskId(null);
    setForm(emptyForm);
    setSubmitError(null);
  };

  const appendRunLog = (line: string) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) {
      return;
    }

    setRunLogs((current) => {
      if (current.includes(trimmedLine)) {
        return current;
      }
      return [...current, trimmedLine];
    });
  };

  const stopRunStream = () => {
    runStreamCleanupRef.current?.();
    runStreamCleanupRef.current = null;
    setIsRunStreamConnecting(false);
  };

  const invalidateTaskData = () => {
      void queryClient.invalidateQueries({ queryKey: channelScrapingKeys.tasks(selectedScrapingServerId) });
      void queryClient.invalidateQueries({ queryKey: channelScrapingKeys.all });
  };

  const closeRunModal = () => {
    stopRunStream();
    setIsRunModalOpen(false);
    setRunTaskLabel('');
    setRunSummary(null);
    setRunLogs([]);
    setRunError(null);
    setIsRunStarting(false);
  };

  const startRunLogStream = (runId: string, scrapingServerId?: string | null) => {
    stopRunStream();
    setIsRunStreamConnecting(true);
    runStreamCleanupRef.current = channelScrapingService.streamTaskRunLogs(runId, scrapingServerId, {
      onRun: (line) => {
        appendRunLog(line);
        setIsRunStreamConnecting(false);
      },
      onLog: (line) => {
        appendRunLog(line);
        setIsRunStreamConnecting(false);
      },
      onComplete: async (line) => {
        appendRunLog(line);
        setIsRunStreamConnecting(false);
        try {
          setRunSummary(await channelScrapingService.getTaskRun(runId, scrapingServerId));
        } catch {
          // Keep the existing summary if the refresh fails.
        }
        invalidateTaskData();
      },
      onError: (streamError) => {
        setRunError(streamError.message);
        setIsRunStreamConnecting(false);
      },
    });
  };

  const handleRunTask = async (task: ChannelScrapingTask) => {
    setIsRunModalOpen(true);
    setRunTaskLabel(task.streamName || task.url);
    setRunSummary(null);
    setRunLogs([]);
    setRunError(null);
    setIsRunStarting(true);
    setIsRunStreamConnecting(false);

    try {
      const scrapingServerId = task.scrapingServerId;
      const summary = await runMutation.mutateAsync({ id: task.id, scrapingServerId });
      setRunSummary(summary);

      const [latestSummary, bufferedLogs] = await Promise.all([
        channelScrapingService.getTaskRun(summary.runId, scrapingServerId),
        channelScrapingService.getTaskRunLogs(summary.runId, scrapingServerId),
      ]);

      setRunSummary(latestSummary);
      setRunLogs(
        bufferedLogs
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean)
      );

      if (isTerminalRunStatus(latestSummary.status)) {
        invalidateTaskData();
      } else {
        startRunLogStream(summary.runId, scrapingServerId);
      }
    } catch (runFailure) {
      setRunError(runFailure instanceof Error ? runFailure.message : 'No se pudo ejecutar la tarea');
    } finally {
      setIsRunStarting(false);
    }
  };

  useEffect(() => () => stopRunStream(), []);

  useEffect(() => {
    const container = runLogsContainerRef.current;
    if (!container) {
      return;
    }
    container.scrollTop = container.scrollHeight;
  }, [runLogs, isRunStarting, isRunStreamConnecting]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    try {
      const selectedTargetServer = targetServerLookup.get(form.targetServerId) || targetServers[0];
      if (!selectedTargetServer) {
        setSubmitError('Debe seleccionar un servidor Flussonic válido');
        return;
      }

      if (!selectedScrapingServerId) {
        setSubmitError('Debe seleccionar un servidor de scraping');
        return;
      }

      const payload: ChannelScrapingTaskPayload = {
        url: form.url,
        stopPatternRequest: form.stopPatternRequest,
        server: selectedTargetServer.name,
        streamName: form.streamName,
        frequency: Number(form.frequencyHours),
        userAgent: form.userAgent.trim() || undefined,
        httpHeaders: mapRowsToHeaders(form.headers),
      };
      if (editingTask) {
        await updateMutation.mutateAsync({ id: editingTask.id, data: payload, scrapingServerId: editingTask.scrapingServerId });
      } else {
        payload.scrapingServerIds = [selectedScrapingServerId];
        await createMutation.mutateAsync(payload);
      }
      closeModal();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'No se pudo guardar la tarea');
    }
  };

  const handleDelete = async (task: ChannelScrapingTask) => {
    const taskLabel = task.streamName || task.url;
    if (!window.confirm(`¿Eliminar tarea ${taskLabel}?`)) return;
    await deleteMutation.mutateAsync({ id: task.id, scrapingServerId: task.scrapingServerId });
  };

  const columns: TableColumn<ChannelScrapingTask>[] = [
    {
      key: 'streamName',
      header: 'Stream',
      accessor: (task) => <span className="font-medium text-gray-900">{task.streamName || '-'}</span>,
    },
    {
      key: 'server',
      header: 'Servidor Flussonic',
      accessor: (task) => {
        const label = getTaskServerLabel(task);
        return <span className="block max-w-48 truncate text-sm text-gray-900" title={label}>{label || '-'}</span>;
      },
    },
    {
      key: 'scrapingServer',
      header: 'Servidor scraping',
      accessor: (task) => {
        const label = getTaskScrapingServerLabel(task);
        return <span className="block max-w-48 truncate text-sm text-gray-900" title={label}>{label || '-'}</span>;
      },
    },
    {
      key: 'url',
      header: 'URL',
      accessor: (task) => <span className="block max-w-md truncate text-sm text-gray-900" title={task.url}>{task.url}</span>,
      className: 'max-w-72',
    },
    {
      key: 'lastRunAt',
      header: 'Última ejecución',
      accessor: (task) => <span className="text-sm text-gray-900">{formatDateTime(task.lastRunAt ?? undefined, '-')}</span>,
    },
    {
      key: 'lastStatus',
      header: 'Último estado',
      accessor: (task) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${lastStatusClass(task.lastStatus)}`}>
          {lastStatusLabel(task.lastStatus)}
        </span>
      ),
    },
    {
      key: 'lastDeliveryMode',
      header: 'Entrega',
      accessor: (task) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${deliveryModeClass(task.lastDeliveryMode)}`}>
          {deliveryModeLabel(task.lastDeliveryMode)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'center',
      accessor: (task) => (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" onClick={() => void handleRunTask(task)} disabled={isRunStarting}>
            <Play className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => openEditModal(task)}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="danger" onClick={() => void handleDelete(task)} disabled={deleteMutation.isPending}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
      className: 'w-32',
    },
  ];

  return (
    <TVChannelScrapingLayout>
      <div className="space-y-4">
        {isError && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Error al cargar tareas de scraping'}
          </div>
        )}

        {isLoading ? (
          <div className="border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 shadow">
            Cargando tareas...
          </div>
        ) : (
          <DataTable
            data={filteredTasks}
            columns={columns}
            paginate={false}
            tableStyle="compact"
            showToolbar={false}
            className="shadow"
            headerRenderer={() => (
              <div className="border-b border-gray-200 bg-white">
                <div className="flex flex-col gap-3 px-4 py-2 md:flex-row md:items-center md:justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Tareas de Scraping</h3>
                  <div className="flex flex-col gap-2 md:flex-row md:items-center">
                    <div className="relative md:w-80">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Buscar tareas"
                        className="h-9 w-full rounded-md border border-gray-300 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
                      <RefreshCw className={`mr-1 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                      Refrescar
                    </Button>
                    <Button size="sm" onClick={openCreateModal} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                      <Plus className="mr-1 h-4 w-4" />
                      Nueva tarea
                    </Button>
                  </div>
                </div>
              </div>
            )}
          />
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={editingTask ? 'Editar tarea de scraping' : 'Nueva tarea de scraping'}
          size="xl"
          closeOnBackdrop={!isSubmitting}
          closeOnEsc={!isSubmitting}
        >
          {editingTaskId && isEditingTaskLoading ? (
            <div className="px-2 py-6 text-center text-sm text-gray-500">Cargando tarea...</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 px-2 py-2">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Input label="Stream" value={form.streamName} onChange={(e) => setForm((prev) => ({ ...prev, streamName: e.target.value }))} required />
                <Select
                  label="Servidor Flussonic"
                  value={form.targetServerId || targetServers[0]?.id || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, targetServerId: e.target.value }))}
                  options={targetServerOptions}
                  required
                />
                <Input label="URL" value={form.url} onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))} required />
                <Input
                  label="Frecuencia (horas)"
                  type="number"
                  min="1"
                  value={form.frequencyHours}
                  onChange={(e) => setForm((prev) => ({ ...prev, frequencyHours: e.target.value }))}
                  required
                />
              </div>
              <Input
                label="Patrón de parada"
                value={form.stopPatternRequest}
                onChange={(e) => setForm((prev) => ({ ...prev, stopPatternRequest: e.target.value }))}
                required
              />
              <div className="border-l-2 border-blue-500 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                Las playlists HLS con audio y video separados se detectan automáticamente. La tarea seleccionará las variantes válidas de mayor calidad y publicará el stream mediante FFmpeg.
              </div>
              <Input
                label="User-Agent"
                value={form.userAgent}
                onChange={(e) => setForm((prev) => ({ ...prev, userAgent: e.target.value }))}
                placeholder="Mozilla/5.0 ..."
              />
              <div className="space-y-3 rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Headers adicionales</h3>
                    <p className="text-xs text-gray-500">Se enviarán a Flussonic como pares clave-valor cuando el stream sea actualizado.</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        headers: [...prev.headers, createHeaderRow()],
                      }))
                    }
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Agregar header
                  </Button>
                </div>

                {form.headers.length ? (
                  <div className="space-y-3">
                    {form.headers.map((header) => (
                      <div key={header.id} className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
                        <Input
                          label="Key"
                          value={header.key}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              headers: prev.headers.map((item) =>
                                item.id === header.id ? { ...item, key: e.target.value } : item
                              ),
                            }))
                          }
                          placeholder="Referer"
                        />
                        <Input
                          label="Value"
                          value={header.value}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              headers: prev.headers.map((item) =>
                                item.id === header.id ? { ...item, value: e.target.value } : item
                              ),
                            }))
                          }
                          placeholder="https://example.com"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="danger"
                          className="md:mb-0.5"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              headers: prev.headers.filter((item) => item.id !== header.id),
                            }))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-gray-200 px-3 py-4 text-sm text-gray-500">
                    No hay headers configurados.
                  </div>
                )}
              </div>
              {submitError && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{submitError}</div>}
              <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                <Button type="button" variant="outline" onClick={closeModal} disabled={isSubmitting}>Cancelar</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={isSubmitting}>Guardar</Button>
              </div>
            </form>
          )}
        </Modal>

        <Modal
          isOpen={isRunModalOpen}
          onClose={closeRunModal}
          title={runTaskLabel ? `Ejecución: ${runTaskLabel}` : 'Ejecución manual'}
          size="2xl"
        >
          <div className="space-y-4">
            {runSummary && (
              <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-2">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${runStatusClass(runSummary.status)}`}>
                      {runStatusLabel(runSummary.status)}
                    </span>
                    {isRunStarting || isRunStreamConnecting ? (
                      <span className="text-xs text-gray-500">Conectando logs...</span>
                    ) : null}
                  </div>
                  <div><span className="font-medium text-gray-900">Run ID:</span> <span className="break-all text-gray-700">{runSummary.runId}</span></div>
                  <div><span className="font-medium text-gray-900">Inicio:</span> <span className="text-gray-700">{runSummary.startedAt || '-'}</span></div>
                  <div><span className="font-medium text-gray-900">Fin:</span> <span className="text-gray-700">{runSummary.completedAt || '-'}</span></div>
                </div>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium text-gray-900">URL detectada:</span> <span className="break-all text-gray-700">{runSummary.matchedUrl || '-'}</span></div>
                  <div><span className="font-medium text-gray-900">Modo de entrega:</span> <span className="text-gray-700">{deliveryModeLabel(runSummary.deliveryMode)}</span></div>
                  {runSummary.videoUrl ? (
                    <div><span className="font-medium text-gray-900">Video:</span> <span className="break-all text-gray-700">{runSummary.videoUrl}</span></div>
                  ) : null}
                  {runSummary.audioUrl ? (
                    <div><span className="font-medium text-gray-900">Audio:</span> <span className="break-all text-gray-700">{runSummary.audioUrl}</span></div>
                  ) : null}
                  {runSummary.publisherOutputUrl ? (
                    <div><span className="font-medium text-gray-900">Publicación:</span> <span className="break-all text-gray-700">{runSummary.publisherOutputUrl}</span></div>
                  ) : null}
                  <div><span className="font-medium text-gray-900">Estado servidor:</span> <span className="text-gray-700">{runSummary.serverStatus || '-'}</span></div>
                  {runSummary.error ? (
                    <div><span className="font-medium text-gray-900">Error:</span> <span className="text-red-700">{runSummary.error}</span></div>
                  ) : null}
                </div>
                {runSummary.serverResponse != null ? (
                  <div className="md:col-span-2">
                    <div className="mb-1 text-sm font-medium text-gray-900">Respuesta del servidor</div>
                    <pre className="max-h-40 overflow-auto rounded-md border border-gray-200 bg-white p-3 text-xs text-gray-700 whitespace-pre-wrap">
                      {formatServerResponse(runSummary.serverResponse)}
                    </pre>
                  </div>
                ) : null}
              </div>
            )}

            {runError ? (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{runError}</div>
            ) : null}

            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Logs</h3>
                {isRunStarting || isRunStreamConnecting ? (
                  <span className="text-xs text-gray-500">Escuchando stream...</span>
                ) : null}
              </div>

              <div
                ref={runLogsContainerRef}
                className="max-h-[50vh] overflow-auto rounded-lg border border-gray-800 bg-[#0b1220] p-3 font-mono text-xs shadow-inner"
              >
                {!runLogs.length && !runError ? (
                  <div className="text-sm text-gray-400">
                    {isRunStarting || isRunStreamConnecting ? 'Esperando logs...' : 'Sin logs.'}
                  </div>
                ) : null}

                {runLogs.map((log, index) => (
                  <pre key={`${index}-${log}`} className={`whitespace-pre-wrap break-words leading-5 ${lineClassName(log)}`}>
                    {log}
                  </pre>
                ))}
              </div>
            </div>

            <div className="flex justify-end border-t border-gray-200 pt-4">
              <Button type="button" variant="outline" onClick={closeRunModal}>Cerrar</Button>
            </div>
          </div>
        </Modal>
      </div>
    </TVChannelScrapingLayout>
  );
};

export default TVChannelScrapingTasks;
